import React, { useContext, useEffect, useRef } from 'react';
import { useDataModel, QueryContext, extend } from '@thd-nucleus/data-sources';
import classNames from 'classnames';
import { useStore, useConfigService } from '@thd-nucleus/experience-context';
import { Col, Row } from '@thd-olt-component-react/grid';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { constants, skipFn } from './CollectionHelpers';
import { ShopByRoomCollectionDesktop } from './shop-by-room/ShopByRoomDesktop';
import { ListViewCollectionDesktop } from './list-view/ListViewDesktop';
import { IntegratedBrandCollectionDesktop } from './integrated-brand/IntegratedBrandDesktop';
import { GroupedViewCollectionDesktop } from './grouped-view/DesktopGroupedView';
import { CollectionDefaultProps, CollectionPropTypes } from './CollectionPropTypes';
import { BathCollectionData } from './integrated-brand-bath/IbtData';
import { dataModel } from './dataModel';
import { IntegratedBrandBath } from './integrated-brand-bath/IntegratedBrandBath';
import './collection.scss';

export const CollectionDesktop = ({ collectionId, preview }) => {
  const isBathCollectionPoCEnabled = useConfigService('fs:enableBathCollectionPoC');
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('collection.ready');
  }, []);
  const { isClientResolved } = useContext(QueryContext);
  const store = useStore();
  const { storeId } = store;

  const loadedStoreId = useRef(storeId);
  const spaceCollectionId = collectionId.includes('id') ? collectionId.replace(/\D/g, '')
    .trim() : '';
  const collectionVariables = {
    collectionId,
    storeId
  };
  if (spaceCollectionId) {
    collectionVariables.preview = preview;
  }
  const {
    data,
    error,
    loading,
    variables
  } = useDataModel('collection', {
    variables: collectionVariables,
    ssr: true
  });

  if (data && !loading && variables?.storeId && variables.storeId !== '8119') {
    loadedStoreId.current = variables.storeId;
  }
  const { collection } = data || {};
  useEffect(() => {
    if (collection) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('collections.data', collection);
    }
  }, [collection?.products?.length]);

  if ((error && !data) || !data) {
    return null;
  }

  const { metadata } = collection || {};

  const { type } = metadata || {};
  const greyBg = classNames({ 'grey-bg': spaceCollectionId });
  const shouldSkip = (opts) => skipFn(opts, { isClientResolved });
  const bathPoCCollection = BathCollectionData.find((item) => item.collectionId === collectionId);
  const IBTCollection = !isBathCollectionPoCEnabled || (isBathCollectionPoCEnabled && !bathPoCCollection);

  return (
    <Row className="collections" data-component="CollectionDesktop">
      {/* TODO: Update breadcrumbs to take collectionId and put back in the experience */}
      <Col className={greyBg}>
        <Breadcrumbs collectionId={collectionId} />
      </Col>
      {
        spaceCollectionId && (
          <ShopByRoomCollectionDesktop
            collection={collection}
            spaceCollectionId={collectionId}
            preview={preview}
            skipFn={shouldSkip}
            storeId={storeId}
          />
        )
      }
      {type === constants.LIST_VIEW
      && (
        <ListViewCollectionDesktop
          storeId={storeId}
          collectionId={collectionId}
          collection={collection}
          skipFn={shouldSkip}
        />
      )}
      {type === constants.INTEGRATED_BRAND && IBTCollection
      && (
        <IntegratedBrandCollectionDesktop
          storeId={loadedStoreId.current}
          collectionId={collectionId}
          collection={collection}
          skipFn={shouldSkip}
        />
      )}
      {type === constants.INTEGRATED_BRAND && !IBTCollection
      && (
        <Col flatten>
          <IntegratedBrandBath
            storeId={storeId}
            collectionId={collectionId}
            collection={collection}
          />
        </Col>
      )}
      {[constants.GROUPED_VIEW, constants.GROUPED_LIST_VIEW].indexOf(type) > -1
      && (
        <GroupedViewCollectionDesktop
          storeId={storeId}
          collectionId={collectionId}
          collection={collection}
          skipFn={shouldSkip}
        />
      )}
    </Row>
  );
};

CollectionDesktop.displayName = 'CollectionDesktop';

CollectionDesktop.dataModel = extend({}, dataModel, IntegratedBrandBath);

CollectionDesktop.propTypes = CollectionPropTypes;

CollectionDesktop.defaultProps = CollectionDefaultProps;